import {Component, InjectionToken, forwardRef, input, model, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"

// Public API token to let options talk to their group
export interface RadioGroupApi {
    select(value: unknown): void
    isSelected(value: unknown): boolean
    name(): string
    disabled(): boolean
    value(): unknown
}

export const RADIO_GROUP = new InjectionToken<RadioGroupApi>("CM_RADIO_GROUP")

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-radio-group",
    templateUrl: "./radio-group.component.html",
    styleUrls: ["./radio-group.component.scss"],
    imports: [FormsModule],
    providers: [{provide: RADIO_GROUP, useExisting: forwardRef(() => RadioGroupComponent)}],
    exportAs: "cmRadioGroup",
})
export class RadioGroupComponent<T> {
    // Two-way bound selected value
    readonly $value = model.required<T>({alias: "value"})
    readonly valueChange = output<T>()

    // Optional group-wide name/disabled inputs
    readonly $name = input<string | undefined>(undefined)
    readonly $disabled = input<boolean>(false)

    // Internal generated name if none provided
    private static _nextId = 0
    private readonly _autoName = signal<string>(`cm-radio-group-${RadioGroupComponent._nextId++}`)

    onValueChange() {
        this.valueChange.emit(this.$value())
    }

    // API used by options
    select = (value: T) => {
        if (this.$disabled()) return
        if (this.$value() !== value) {
            this.$value.set(value)
            this.valueChange.emit(value)
        }
    }

    isSelected = (value: T): boolean => Object.is(this.$value(), value)

    name = (): string => this.$name() ?? this._autoName()

    disabled = (): boolean => this.$disabled()

    value = (): T => this.$value()
}
